import {
  ChevronRight,
  BookOpen,
  Radar,
  HelpCircle,
  LogOut,
  MessageCircleQuestionMark,
  Mail,
  CalendarDays,
  Megaphone,
  Settings,
  SunMoon,
} from 'lucide-react';

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { IndexPath } from '@/Config/app/router/index.path';
import { Avatar, AvatarFallback, AvatarImage } from '@/Domain/components/Shared/Avatar/Avatar';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLink,
  DropdownMenuTrigger,
  DropdownMenu,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
  DropdownMenuSubContent,
} from '@/Domain/components/Shared/DropdownMenu/DropdownMenu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/Domain/components/Shared/Sidebar/Sidebar';
import { Skeleton } from '@/Domain/components/Shared/Skeleton/Skeleton';
import { ThemeMenuItems } from '@/Domain/components/Shared/ThemeSelect/ThemeMenuItems';
import DiscordIcon from '@/Domain/components/icons/DiscordIcon';
import WhatsAppIcon from '@/Domain/components/icons/WhatsAppIcon';
import { UserHelper } from '@/Domain/helper/User.helper';
import { useLogout } from '@/Domain/hooks/Auth/useLogout';
import { useUser } from '@/Domain/hooks/User/useUser.hook';
import { useSupportWidgetStore } from '@/Domain/store/supportWidget.store';
import { useThemeStore } from '@/Domain/store/theme.store';

export function SidebarUserMenu() {
  const { t } = useTranslation('sidebar_menu');
  const { isMobile, portalContainer } = useSidebar();
  const { handleLogout } = useLogout();
  const { toggleOpen, setFeedbackPopoverOpen } = useSupportWidgetStore();
  const getResolvedTheme = useThemeStore((state) => state.getResolvedTheme);

  const { user } = useUser();

  const openDemoCall = useCallback(async () => {
    const { getCalApi } = await import('@calcom/embed-react');
    const cal = await getCalApi();
    cal('ui', { theme: getResolvedTheme() });
    cal('modal', { calLink: 'team/quickchatai/product-demo' });
  }, [getResolvedTheme]);

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton className="data-[state=open]:bg-sidebar-accent data-[state=open]:px-2 group-data-[collapsible=icon]:!p-1.5">
              <div className="flex aspect-square size-5 items-center justify-center">
                <Avatar className="size-5 rounded-md">
                  <AvatarImage src={user?.avatar_url || undefined} />
                  <AvatarFallback className="rounded-md text-xs">
                    {user?.first_name?.charAt(0) || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
              </div>
              <div className="flex flex-1 min-w-0 items-center text-left text-sm">
                {user ? (
                  <span className="truncate font-medium text-sidebar-foreground">
                    {UserHelper.getFullName(user) || user.email}
                  </span>
                ) : (
                  <Skeleton className="h-4 w-24" />
                )}
              </div>
              <ChevronRight className="ml-auto size-4 text-sidebar-foreground/50 transition-transform duration-200 group-data-[state=open]/menu-item:rotate-90" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
            container={isMobile ? portalContainer : undefined}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={user?.avatar_url || undefined} />
                  <AvatarFallback className="rounded-lg">
                    {user?.first_name?.charAt(0) || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col flex-1 min-w-0 text-left text-sm leading-tight">
                  <span className="truncate font-semibold text-popover-foreground">
                    {UserHelper.getFullName(user) || user?.email}
                  </span>
                  {UserHelper.getFullName(user) && (
                    <span className="truncate text-xs text-muted-foreground">{user?.email}</span>
                  )}
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuLink to={IndexPath.settings}>
              <Settings />
              {t('menu.account.settings')}
            </DropdownMenuLink>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <HelpCircle />
                {t('menu.account.contact')}
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  <DropdownMenuItem onClick={toggleOpen}>
                    <MessageCircleQuestionMark />
                    {t('menu.account.support_ai')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      window.open(
                        'https://wa.me/message/LHARULDFIUEZL1',
                        '_blank',
                        'noopener,noreferrer',
                      )
                    }
                  >
                    <WhatsAppIcon />
                    {t('menu.account.whatsapp')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={openDemoCall}>
                    <CalendarDays />
                    {t('menu.account.call')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => (window.location.href = `mailto:support@quickchat.ai`)}
                  >
                    <Mail />
                    {t('menu.account.email')}
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
            <DropdownMenuItem onClick={() => setFeedbackPopoverOpen(true)}>
              <Megaphone />
              {t('menu.account.feedback')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                window.open('https://docs.quickchat.ai', '_blank', 'noopener,noreferrer')
              }
            >
              <BookOpen />
              {t('menu.account.documentation')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                window.open('https://status.quickchat.ai', '_blank', 'noopener,noreferrer')
              }
            >
              <Radar />
              {t('menu.account.status')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                window.open('https://discord.gg/KqkHwvPRNH', '_blank', 'noopener,noreferrer')
              }
            >
              <DiscordIcon />
              {t('menu.account.discord')}
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <SunMoon />
                {t('menu.account.theme')}
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  <ThemeMenuItems />
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut />
              {t('menu.account.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
